import React from "react";
import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
    Search,
    ClipboardList,
    Rocket,
    Sparkles,
    ShieldCheck,
    Zap,
    Handshake,
} from "lucide-react";
import Navbar from "../Components/Navbar";
import Call from "../Components/Call";
import Footer from "../Components/Footer";
import "../Styling/Work.css";
import ChatbotWidget from "./ChatComponent";

gsap.registerPlugin(ScrollTrigger);

const containerVariants = {
    hidden: { opacity: 1 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0,
            delayChildren: 0
        }
    }
};

const itemVariants = {
    hidden: { opacity: 1, y: 0 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0 }
    }
};

export default function Work() {
    const canvasRef = React.useRef(null);
    const mainRef = React.useRef(null);

    React.useEffect(() => {
        // --- Background Canvas Animation ---
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx_canvas = canvas.getContext("2d");
        let width, height, particles = [];

        const resize = () => {
            width = canvas.width = window.innerWidth;
            height = canvas.height = window.innerHeight;
        };

        window.addEventListener("resize", resize);
        resize();

        for (let i = 0; i < 40; i++) {
            particles.push({
                x: Math.random() * width,
                y: Math.random() * height,
                vx: (Math.random() - 0.5) * 0.5,
                vy: (Math.random() - 0.5) * 0.5,
                radius: Math.random() * 2 + 1,
                color: ["#5b4cff", "#00bfff", "#3d2eb5"][Math.floor(Math.random() * 3)]
            });
        }

        const animate = () => {
            ctx_canvas.clearRect(0, 0, width, height);
            particles.forEach((p, i) => {
                p.x += p.vx; p.y += p.vy;
                if (p.x < 0 || p.x > width) p.vx *= -1;
                if (p.y < 0 || p.y > height) p.vy *= -1;
                ctx_canvas.beginPath();
                ctx_canvas.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
                ctx_canvas.fillStyle = p.color;
                ctx_canvas.globalAlpha = 0.4;
                ctx_canvas.fill();
                for (let j = i + 1; j < particles.length; j++) {
                    const p2 = particles[j];
                    const dist = Math.hypot(p.x - p2.x, p.y - p2.y);
                    if (dist < 150) {
                        ctx_canvas.beginPath();
                        ctx_canvas.moveTo(p.x, p.y);
                        ctx_canvas.lineTo(p2.x, p2.y);
                        ctx_canvas.strokeStyle = p.color;
                        ctx_canvas.globalAlpha = (1 - dist / 150) * 0.2;
                        ctx_canvas.lineWidth = 0.5;
                        ctx_canvas.stroke();
                    }
                }
            });
            requestAnimationFrame(animate);
        };
        animate();

        // --- GSAP Scroll/Hero Animations ---
        const ctx_gsap = gsap.context((self) => {
            // Floating Animation for Cards
            gsap.to(".main-card", {
                y: -15,
                duration: 2.5,
                repeat: -1,
                yoyo: true,
                ease: "power1.inOut"
            });

            gsap.to(".card-1", {
                y: 12,
                x: 8,
                duration: 3,
                repeat: -1,
                yoyo: true,
                ease: "power1.inOut"
            });

            gsap.to(".card-2", {
                y: -12,
                x: -8,
                duration: 3.5,
                repeat: -1,
                yoyo: true,
                ease: "power1.inOut"
            });

            // Ambient blobs movement
            gsap.to(".Work-blob", {
                x: "random(-100, 100)",
                y: "random(-100, 100)",
                duration: 10,
                repeat: -1,
                yoyo: true,
                ease: "sine.inOut",
                stagger: 0.5
            });

            // Background mesh rotation
            gsap.to(".Work-mesh-overlay", {
                rotation: 360,
                duration: 60,
                repeat: -1,
                ease: "none"
            });

            // Make all sections visible instantly
            const sections = self.selector('section');
            sections.forEach(section => {
                gsap.set(section, { opacity: 1, y: 0 });
            });

            ScrollTrigger.refresh();
        }, mainRef);

        const refreshTimer = setTimeout(() => {
            ScrollTrigger.refresh();
        }, 500);

        return () => {
            window.removeEventListener("resize", resize);
            ctx_gsap.revert();
            clearTimeout(refreshTimer);
        };
    }, []);

    const processSteps = [
        {
            id: "01",
            icon: Search,
            title: "Demo & Evaluation",
            subtitle: "Limited, NDA-based sandbox access.",
            details: [
                "Apply for demo access via our request form",
                "Manual review and NDA signing process",
                "Limited sandbox credentials issued",
                "Monitored evaluation period",
                "Access to select tools based on use case"
            ],
            footer: "Paid Licensing <br/> Only"
        },
        {
            id: "02",
            icon: ClipboardList,
            title: "Contract & Licensing",
            subtitle: "SaaS, white-label, or enterprise agreements.",
            details: [
                "Commercial discussion based on evaluation results",
                "Proposal outlining licensing terms and pricing",
                "Contract negotiation and finalization",
                "Technical requirements gathering",
                "Implementation timeline planning"
            ],
            footer: "White-Label & <br/> Enterprise"
        },
        {
            id: "03",
            icon: Rocket,
            title: "Deployment & Scale",
            subtitle: "Customization, integrations, long-term support.",
            details: [
                "System configuration and customization",
                "Integration with existing tech stack",
                "Team training and onboarding",
                "Ongoing technical support and maintenance",
                "Roadmap alignment and feature requests"
            ],
            footer: "Strategic Partnerships <br/> Preferred"
        }
    ];

    const principles = [
        {
            icon: Zap,
            title: "No Free Trials",
            desc: "Demo access is strictly controlled, monitored, and issued manually after rigorous qualification. We prioritize depth over volume."
        },
        {
            icon: ShieldCheck,
            title: "Enterprise Focus",
            desc: "We engineer exclusively for serious operators agencies, enterprises, and strategic partners who require production-grade stability."
        },
        {
            icon: Handshake,
            title: "Long-Term Partnerships",
            desc: "Our systems are designed for businesses that need production ready solutions, not experiments. We grow alongside our partners."
        }
    ];

    return (
        <div className="Work-wrapper" ref={mainRef}>
            <Navbar />

            {/* GSAP Animated Background Canvas */}
            <canvas ref={canvasRef} style={{ position: 'fixed', top: 0, left: 0, zIndex: 0, pointerEvents: 'none', opacity: 0.4 }} />

            {/* Cinematic Hero Section */}
            <section className="Work-header-section">
                <div className="Work-mesh-container">
                    <div className="Work-mesh-overlay"></div>
                    <div className="Work-blob blob-purple"></div>
                    <div className="Work-blob blob-blue"></div>
                    <div className="Work-blob blob-cyan"></div>
                </div>

                <div className="container">
                    <div className="Work-hero-layout">
                        <motion.div
                            className="Work-hero-content"
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                        >
                            <motion.div variants={itemVariants} className="Work-badge-premium">
                                <Sparkles size={16} /> Engineering Excellence
                            </motion.div>
                            <motion.h1 variants={itemVariants} className="Work-header-title">
                                Engineered For <span className="magenta-gradient">Serious Operators</span>
                            </motion.h1>
                            <motion.p variants={itemVariants} className="Work-header-desc">
                                A structured approach to evaluation, licensing, and deployment. We eliminate the gap between business requirements and technical execution with a rigorous delivery model built for scale.
                            </motion.p>
                            <motion.div variants={itemVariants} className="Work-hero-ctas">
                                <a href="https://calendly.com/digi-dreamworks/onboarding-call" className="Work-btn-primary">
                                    Book Methodology Demo <Rocket size={20} />
                                </a>
                                <div className="Work-trust-label">
                                    <ShieldCheck size={18} /> Production-Grade Alignment
                                </div>
                            </motion.div>
                        </motion.div>

                        <div className="Work-hero-visual">
                            <div className="visual-card main-card">
                                <Zap size={40} />
                                <span>Methodology</span>
                            </div>
                            <div className="visual-card secondary-card card-1">
                                <Rocket size={24} />
                                <span>Scale</span>
                            </div>
                            <div className="visual-card secondary-card card-2">
                                <Handshake size={24} />
                                <span>Partnership</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Process Steps Section */}
            <section className="Work-process-section">
                <div className="container">
                    <div className="Work-steps-grid">
                        {processSteps.map((step, idx) => (
                            <motion.div
                                key={idx}
                                className="Work-step-card"
                                whileHover={{ y: -10 }}
                                transition={{ duration: 0.4 }}
                            >
                                <div className="Work-step-number">{step.id}</div>
                                <div className="Work-card-icon-wrapper">
                                    <step.icon size={28} className="Work-card-icon" />
                                </div>
                                <h3 className="Work-step-title">{step.title}</h3>
                                <p className="Work-step-subtitle">{step.subtitle}</p>
                                <ul className="Work-step-details">
                                    {step.details.map((detail, dIdx) => (
                                        <li key={dIdx} className="Work-step-detail-item">{detail}</li>
                                    ))}
                                </ul>
                                <div className="Work-step-footer" dangerouslySetInnerHTML={{ __html: step.footer }} />
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Operating Principles Section */}
            <section className="Work-principles-section">
                <div className="container">
                    <div className="Work-section-header">
                        <h2 className="Work-principles-title">Our Operating Principles</h2>
                        <div className="Work-title-underline" />
                    </div>

                    <div className="Work-principles-grid">
                        {principles.map((principle, idx) => (
                            <motion.div
                                key={idx}
                                className="Work-principle-card"
                                whileHover={{ y: -5, boxShadow: "0 20px 40px rgba(0,0,0,0.05)" }}
                            >
                                <div className="Work-principle-icon-wrapper">
                                    <principle.icon className="Work-principle-icon" />
                                </div>
                                <h3 className="Work-principle-card-title">{principle.title}</h3>
                                <p className="Work-principle-desc">{principle.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <div className="LD-divider" />
            <ChatbotWidget />
            <Call />
            <Footer />
        </div>
    );
}
